/**
 * usePageTheme Hook
 *
 * Automatically applies the correct theme based on the current page route.
 * Themes are defined in utils/page-themes.ts
 */

import { useEffect } from 'react';
import { applyTheme } from '@smallbiz/brand-kit/themes';
import { getThemeForPage } from '../utils/page-themes';

/**
 * Apply page-specific theme based on current pathname
 *
 * This hook listens to pathname changes and automatically applies
 * the corresponding theme defined in page-themes.ts
 *
 * @param pathname - Current pathname (from router or window.location.pathname)
 *
 * @example
 * ```tsx
 * function App() {
 *   const [currentPath, setCurrentPath] = useState(window.location.pathname);
 *
 *   // Apply theme based on current page
 *   usePageTheme(currentPath);
 *
 *   // ... rest of app
 * }
 * ```
 */
export function usePageTheme(pathname: string): void {
  useEffect(() => {
    // Get theme for current page
    const theme = getThemeForPage(pathname);

    // Apply the theme
    applyTheme(theme);

    // Optional: Log theme changes in development
    if (import.meta.env.DEV) {
      console.log(`[Theme] Page: ${pathname} → Theme: ${theme}`);
    }
  }, [pathname]);
}

/**
 * Get current theme without applying it
 *
 * Useful for reading the theme without side effects
 *
 * @param pathname - Current pathname
 * @returns Theme ID for the given pathname
 */
export function useCurrentTheme(pathname: string): string {
  return getThemeForPage(pathname);
}
